import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";
import { useProjects } from "@/hooks/useProjects";
import type { MetricsData } from "@/hooks/useMetrics";
import type { ProjectStats } from "@/hooks/useProjectStats";
import {
  buildPortfolio,
  type AttentionThresholds,
  type PortfolioResult,
  type ProjectInput,
} from "@/lib/portfolio";

export interface PortfolioState {
  portfolio: PortfolioResult;
  loading: boolean;
}

const TODAY_ISO = new Date().toISOString().slice(0, 10);

export function usePortfolio(thresholds?: AttentionThresholds): PortfolioState {
  const { projects, loading: projLoading } = useProjects();
  const slugs = useMemo(() => projects.map((p) => p.slug), [projects]);

  const statsResults = useQueries({
    queries: slugs.map((slug) => ({
      queryKey: queryKeys.projects.stats(slug),
      queryFn: () => api<ProjectStats>(`/api/v2/projects/${slug}/stats`),
      enabled: slug.length > 0,
      staleTime: 60_000,
      retry: 1,
    })),
  });

  const metricsResults = useQueries({
    queries: slugs.map((slug) => ({
      queryKey: queryKeys.metrics.dashboard(slug),
      queryFn: () => {
        const params = new URLSearchParams({ project_id: slug });
        return api<MetricsData>(`/api/v2/metrics?${params.toString()}`);
      },
      enabled: slug.length > 0,
      staleTime: 60_000,
      retry: 1,
    })),
  });

  const loading =
    projLoading ||
    statsResults.some((r) => r.isLoading) ||
    metricsResults.some((r) => r.isLoading);

  const portfolio = useMemo(() => {
    const inputs: ProjectInput[] = projects.map((p, i) => {
      const stats = statsResults[i]?.data;
      const metrics = metricsResults[i]?.data;

      const gatePassRate =
        stats && stats.gate_pass_rate.pass_count + stats.gate_pass_rate.fail_count > 0
          ? (stats.gate_pass_rate.pass_count /
              (stats.gate_pass_rate.pass_count + stats.gate_pass_rate.fail_count)) *
            100
          : null;

      const cycleKpi = metrics?.kpis.find((k) => k.id === "cycle_time");
      const cycleDelta = cycleKpi?.delta ?? null;

      const lastMember = stats?.adoption.members
        .map((m) => m.last_active)
        .sort()
        .at(-1);
      const lastActivityDate = lastMember ?? null;

      return {
        slug: p.slug,
        name: p.name,
        gatePassRate,
        cycleDelta,
        lastActivityDate,
      };
    });

    return buildPortfolio(inputs, TODAY_ISO, thresholds);
  }, [projects, statsResults, metricsResults, thresholds]);

  return { portfolio, loading };
}
